// EventCreatePage — /events/new
//
// WS-B Chunk 6 UI. Two-step create flow:
//   Step 1 — HandlePicker (only fires when the player has no handle yet)
//   Step 2 — Event form → POST /create-draft-event → navigate to
//            /event/:eventId (the Chunk 4 landing → publish surface)
//
// Player-role gating per CLAUDE.md § Three identity roles:
//   role='player'       → can pick 'outing' or 'league'
//   role='course'       → can pick 'outing', 'league', 'event'
//   role='organization' → same as course
//   role='admin'        → same as course (TournyFlex owner; superset)
// The dropdown is filtered client-side; the server enforces the same rule.
//
// Slug auto-derives from the event name as the user types, but stays
// editable — organizers may want a custom URL slug (e.g., "2026-open").
// Real-time uniqueness check (owner_id, year, slug) via db().

import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import { db } from '../lib/db'
import HandlePicker from '../components/HandlePicker'

const SLUG_CHECK_DEBOUNCE_MS = 350

function slugifyName(raw) {
  return String(raw || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 32)
    .replace(/-+$/, '')
}

const TYPE_OPTIONS = [
  { value: 'outing', label: 'Outing', help: 'A friendly outing or one-off tournament.' },
  { value: 'league', label: 'League', help: 'A recurring season with the same roster.' },
  { value: 'event',  label: 'Event',  help: 'A hosted event (charity, corporate). Admin roles only.', adminOnly: true },
]

const GROUP_MODE_OPTIONS = [
  { value: 'random',              label: 'Random each round' },
  { value: 'rotating',            label: 'Rotating — everyone plays everyone' },
  { value: 'same_across_rounds',  label: 'Same groups every round' },
  { value: 'manual',              label: 'Manual — I\'ll set groups myself' },
]

export default function EventCreatePage() {
  const { player, refreshPlayer } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const [handleReady, setHandleReady] = useState(!!player?.handle)

  // Refresh when the player row changes (e.g., after HandlePicker.onDone
  // triggers a refetch upstream).
  useEffect(() => {
    setHandleReady(!!player?.handle)
  }, [player?.handle])

  // Form state — seed `type` from ?type=... query param so the "Set up
  // League" tile on HomePage lands the user on a pre-selected league form.
  // Server-side role gate still enforces who can actually create each type
  // at submit time.
  const now = new Date()
  const initialTypeParam = searchParams.get('type')
  const initialType = ['outing', 'league', 'event'].includes(initialTypeParam)
    ? initialTypeParam
    : 'outing'
  const [form, setForm] = useState({
    name: '',
    type: initialType,
    year: now.getUTCFullYear(),
    slug: '',
    slugTouched: false,
    players_scheduled: '',
    // Rounds defaults to 1 — matches the free-tier cap and the far most
    // common outing shape. Organizer can bump up for multi-round events.
    rounds_scheduled: 1,
    group_formation_mode: 'random',
  })
  const [slugCheck, setSlugCheck] = useState({ state: 'idle', reason: null })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }))

  const roleIsAdmin = player?.role === 'course' || player?.role === 'organization' || player?.role === 'admin'
  const availableTypes = useMemo(
    () => TYPE_OPTIONS.filter(t => !t.adminOnly || roleIsAdmin),
    [roleIsAdmin]
  )

  // Auto-slug from name until the user manually edits the slug.
  useEffect(() => {
    if (form.slugTouched) return
    const s = slugifyName(form.name)
    if (s !== form.slug) setForm(prev => ({ ...prev, slug: s }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.name])

  // Real-time slug uniqueness check.
  useEffect(() => {
    const s = form.slug.trim().toLowerCase()
    if (!s || !player?.id) { setSlugCheck({ state: 'idle', reason: null }); return }
    setSlugCheck({ state: 'checking', reason: null })
    const t = setTimeout(async () => {
      try {
        const { data } = await db('check_slug_available', {
          owner_id: player.id, year: form.year, slug: s,
        })
        if (data?.available) setSlugCheck({ state: 'available', reason: null })
        else setSlugCheck({ state: 'unavailable', reason: data?.reason || 'taken' })
      } catch {
        setSlugCheck({ state: 'idle', reason: null })
      }
    }, SLUG_CHECK_DEBOUNCE_MS)
    return () => clearTimeout(t)
  }, [form.slug, form.year, player?.id])

  const canSubmit = form.name.trim().length > 0
    && (form.slug || '').length > 0
    && slugCheck.state !== 'unavailable'
    && Number(form.players_scheduled) > 0
    && Number(form.rounds_scheduled) > 0

  const submit = async () => {
    if (!canSubmit || busy) return
    setBusy(true)
    setError(null)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) {
        setError('Please sign in again to continue.')
        setBusy(false)
        return
      }
      const res = await fetch('/.netlify/functions/create-draft-event', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          name: form.name.trim(),
          type: form.type,
          year: Number(form.year),
          slug: form.slug || undefined,
          players_scheduled: Number(form.players_scheduled),
          rounds_scheduled: Number(form.rounds_scheduled),
          group_formation_mode: form.group_formation_mode,
        }),
      })
      const json = await res.json()
      if (!res.ok) {
        if (json.code === 'slug_conflict') {
          setSlugCheck({ state: 'unavailable', reason: 'taken' })
        }
        if (json.code === 'need_handle') {
          setHandleReady(false)
        }
        setError(json.error || 'Could not create event.')
        setBusy(false)
        return
      }
      navigate(`/event/${json.event_id}`)
    } catch (e) {
      setError(e.message || 'Network error.')
      setBusy(false)
    }
  }

  // Step 1: handle picker
  if (!handleReady) {
    return (
      <div className="page">
        <div className="container">
          <div className="page-header">
            <p className="eyebrow">Create event · Step 1 of 2</p>
            <h1>Pick a handle</h1>
          </div>
          <HandlePicker
            suggestedFrom={player?.first_name || player?.name}
            onDone={async () => {
              // Refetch the player row so the parent AuthContext sees the
              // new handle; step 2 unlocks on the next render.
              try { await refreshPlayer?.() } catch {}
              setHandleReady(true)
            }}
          />
          <button
            className="btn btn-ghost btn-full"
            style={{ marginTop: 16 }}
            onClick={() => navigate('/events')}
          >
            Cancel
          </button>
        </div>
      </div>
    )
  }

  // Step 2: event form
  const slugFeedback = (() => {
    switch (slugCheck.state) {
      case 'idle': return null
      case 'checking': return <span className="text-xs text-muted">Checking…</span>
      case 'available': return <span className="text-xs" style={{ color: 'var(--electric-green, #39FF14)' }}>Available</span>
      case 'unavailable':
        if (slugCheck.reason === 'bad_format') return <span className="text-xs" style={{ color: 'var(--danger, #b23)' }}>Slug format is invalid.</span>
        return <span className="text-xs" style={{ color: 'var(--danger, #b23)' }}>You already have an event with that slug in {form.year}.</span>
      default: return null
    }
  })()

  return (
    <div className="page">
      <div className="container">
        <div className="page-header">
          <p className="eyebrow">Create event · Step 2 of 2</p>
          <h1>Event details</h1>
          {player?.handle && (
            <p className="text-muted text-sm" style={{ fontFamily: 'var(--font-mono)' }}>
              URL: tournyflex.com/e/{player.handle}/{form.year}/{form.slug || '…'}
            </p>
          )}
        </div>

        <div className="card" style={{ marginBottom: 16 }}>
          <p className="text-xs text-muted" style={{ marginBottom: 4 }}>Name</p>
          <input
            className="input"
            value={form.name}
            onChange={(e) => set('name', e.target.value)}
            placeholder="Church Outing"
            maxLength={200}
            style={{ marginBottom: 12 }}
          />

          <p className="text-xs text-muted" style={{ marginBottom: 4 }}>Type</p>
          <select
            className="input"
            value={form.type}
            onChange={(e) => set('type', e.target.value)}
            style={{ marginBottom: 4 }}
          >
            {availableTypes.map(t => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
          <p className="text-xs text-muted" style={{ marginBottom: 12 }}>
            {availableTypes.find(t => t.value === form.type)?.help}
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,2fr)', gap: 12, marginBottom: 12 }}>
            <div>
              <p className="text-xs text-muted" style={{ marginBottom: 4 }}>Year</p>
              <input
                className="input"
                type="number"
                min={now.getUTCFullYear()}
                max={now.getUTCFullYear() + 5}
                value={form.year}
                onChange={(e) => set('year', Number(e.target.value))}
              />
            </div>
            <div>
              <p className="text-xs text-muted" style={{ marginBottom: 4 }}>URL slug</p>
              <input
                className="input"
                value={form.slug}
                onChange={(e) => setForm(prev => ({
                  ...prev,
                  slug: e.target.value.toLowerCase().replace(/\s+/g, '-'),
                  slugTouched: true,
                }))}
                placeholder="church-outing"
                maxLength={32}
                style={{ fontFamily: 'var(--font-mono)' }}
              />
              <div style={{ minHeight: 18, marginTop: 4 }}>{slugFeedback}</div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 12, marginBottom: 12 }}>
            <div>
              <p className="text-xs text-muted" style={{ marginBottom: 4 }}>Players</p>
              <input
                className="input"
                type="number"
                min={1}
                max={500}
                value={form.players_scheduled}
                onChange={(e) => set('players_scheduled', e.target.value)}
                placeholder="12"
              />
            </div>
            <div>
              <p className="text-xs text-muted" style={{ marginBottom: 4 }}>Rounds</p>
              <input
                className="input"
                type="number"
                min={1}
                max={60}
                value={form.rounds_scheduled}
                onChange={(e) => set('rounds_scheduled', e.target.value)}
                placeholder="1"
              />
            </div>
          </div>

          <p className="text-xs text-muted" style={{ marginBottom: 4 }}>Group formation</p>
          <select
            className="input"
            value={form.group_formation_mode}
            onChange={(e) => set('group_formation_mode', e.target.value)}
            style={{ marginBottom: 12 }}
          >
            {GROUP_MODE_OPTIONS.map(m => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>

          {/* T2 (App Store 3.1.1): FREE launch — no in-app purchase, so every
              event publishes free regardless of player or round count. No fee
              preview, no Stripe-checkout steering. */}
          <div style={{
            background: 'var(--green-deep)',
            borderLeft: '3px solid var(--gold)',
            padding: '10px 12px',
            borderRadius: 6,
            marginBottom: 4,
          }}>
            <p className="text-sm" style={{ margin: 0, fontWeight: 600 }}>
              Publish cost: <span style={{ color: 'var(--gold)' }}>Free</span>
            </p>
            <p className="text-xs text-muted" style={{ margin: '4px 0 0' }}>
              Every event is free to publish at launch — any number of players and rounds.
            </p>
          </div>
        </div>

        {error && (
          <div className="card" style={{ marginBottom: 16, borderColor: 'var(--danger, #b23)' }}>
            <p className="text-sm" style={{ margin: 0, color: 'var(--danger, #b23)' }}>{error}</p>
          </div>
        )}

        <button
          className="btn btn-primary btn-full"
          disabled={!canSubmit || busy}
          onClick={submit}
        >
          {busy ? 'Creating…' : 'Continue to publish'}
        </button>

        <button
          className="btn btn-ghost btn-full"
          style={{ marginTop: 8 }}
          onClick={() => navigate('/events')}
        >
          Cancel
        </button>

        <p className="text-muted text-xs" style={{ textAlign: 'center', marginTop: 16 }}>
          You'll review and publish on the next screen. The event
          stays as a draft — no one sees it until you publish.
        </p>
      </div>
    </div>
  )
}
